import React, { useState, useMemo } from "react";
import { Listbox } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const RowPickListField = (props) => {
  const { fieldName, fieldLabel, fieldMandatory, data, pickListValues } = props;
  const [selectedOption, setSelectedOption] = useState(null);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);


  // Process pickListValues into options array - memoized to prevent infinite loops
  const options = useMemo(() => {
    let processedOptions = [];
    if (pickListValues && typeof pickListValues === 'object') {
      if (Array.isArray(pickListValues)) {
        processedOptions = pickListValues.map((item, index) => ({
          value: item.value || item.key || index,
          label: item.label || item.value || item
        }));
      } else {
        processedOptions = Object.entries(pickListValues).map(([key, value]) => ({
          value: key,
          label: value || key
        }));
      }
    }
    return processedOptions;
  }, [pickListValues]);

  // Set initial value from data
  React.useEffect(() => {
    if (data && data.hasOwnProperty(fieldName) && data[fieldName]) {
      // Handle both string values and object values from API
      let dataValue = data[fieldName];
      if (typeof dataValue === 'object' && dataValue.value) {
        dataValue = dataValue.value;
      }

      const defaultOption = options.find(option => option.value === dataValue);
      if (defaultOption && selectedOption?.value !== defaultOption.value) {
        setSelectedOption(defaultOption);
      }
    }
  }, [data, fieldName, options, selectedOption]);

  const handleSelectionChange = (option) => {
    setSelectedOption(option);
    setIsOpen(false); // Close dropdown after selection
  };

  const filteredOptions = query === '' 
    ? options 
    : options.filter((option) =>
        option.label.toLowerCase().includes(query.toLowerCase())
      );


  return (
    <div className="mb-4">
      <label htmlFor={fieldName} className="block text-sm font-medium text-gray-700 mb-1 text-left">
        {fieldLabel}
        {fieldMandatory && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      {/* Hidden input for form submission */}
      <input 
        type="hidden" 
        name={fieldName} 
        value={selectedOption?.value || ''} 
        required={fieldMandatory}
      />
      
      <Listbox value={selectedOption} onChange={handleSelectionChange}>
        <div className="relative">
          {/* Dropdown Button */}
          <Listbox.Button 
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors text-left bg-white flex items-center justify-between"
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className={`block truncate ${selectedOption ? 'text-gray-900' : 'text-gray-500'}`}>
              {selectedOption ? selectedOption.label : `Select ${fieldLabel}...`}
            </span>
            <ChevronUpDownIcon className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0" aria-hidden="true" />
          </Listbox.Button>
          
          {/* Dropdown Options */}
          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {/* Search Input */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-2">
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-3 py-2 pl-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder={`Search ${fieldLabel.toLowerCase()}...`}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
            </div>
            
            {/* Options List */}
            <div className="max-h-48 overflow-y-auto py-1">
              {/* Clear selection option */}
              <Listbox.Option
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-500'
                  }`
                }
                value={null}
              >
                {({ selected, active }) => (
                  <>
                    <span className="block truncate font-normal italic">
                      Clear selection
                    </span>
                    {selected ? (
                      <span
                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                          active ? 'text-gray-900' : 'text-gray-500'
                        }`}
                      >
                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
              
              {filteredOptions.length === 0 && query !== '' ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                  No options found for "{query}".
                </div>
              ) : (
                filteredOptions.map((option, index) => (
                  <Listbox.Option
                    key={`${option.value}-${index}`}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active ? 'bg-primary-500 text-white' : 'text-gray-900'
                      }`
                    }
                    value={option}
                  >
                    {({ selected, active }) => (
                      <>
                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                          {option.label}
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? 'text-white' : 'text-primary-500'
                            }`}
                          >
                            <CheckIcon className="h-4 w-4" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))
              )}
            </div>
          </Listbox.Options>
        </div>
      </Listbox>
      
      <div className="invalid-feedback mt-1 text-xs text-red-600">
        {fieldLabel} is required.
      </div>
    </div>
  );
};

export default RowPickListField;